"use strict";

export const SUPPORT_TICKET_STATUS_FILTERS = [
    { value: "all", label: "All" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In progress" },
    { value: "waiting_on_player", label: "Waiting on you" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];

export const SUPPORT_TICKET_STATUS_TONES = {
    open: {
        color: "#F6C56F",
        border: "rgba(232,163,61,0.3)",
        background: "rgba(232,163,61,0.12)",
    },
    in_progress: {
        color: "#7CC7FF",
        border: "rgba(74,163,230,0.3)",
        background: "rgba(58,142,204,0.12)",
    },
    waiting_on_player: {
        color: "#CFADFF",
        border: "rgba(167,112,237,0.3)",
        background: "rgba(139,86,205,0.12)",
    },
    resolved: {
        color: "#65D993",
        border: "rgba(34,197,94,0.3)",
        background: "rgba(34,197,94,0.11)",
    },
    closed: {
        color: "#9999A3",
        border: "rgba(255,255,255,0.1)",
        background: "rgba(255,255,255,0.045)",
    },
};

export function getSupportTicketStatusLabel(status) {
    const match = SUPPORT_TICKET_STATUS_FILTERS.find(
        (entry) => entry.value === status,
    );
    return match?.label || String(status || "Open").replace(/_/g, " ");
}

export function getSupportTicketStatusTone(status) {
    return SUPPORT_TICKET_STATUS_TONES[status] ||
        SUPPORT_TICKET_STATUS_TONES.closed;
}

export function formatSupportTicketCategory(category) {
    return String(category || "other")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatSupportTicketDate(value) {
    if (!value) return "Unknown";
    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function formatSupportTicketTime(value) {
    if (!value) return "";
    return new Date(value).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function formatSupportTicketDay(value, now = new Date()) {
    if (!value) return "";
    const date = new Date(value);
    if (date.toDateString() === now.toDateString()) return "Today";
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export function formatSupportTicketRelativeTime(value, nowMs = Date.now()) {
    if (!value) return "Unknown";
    const delta = Math.max(0, nowMs - new Date(value).getTime());
    if (delta < 60 * 1000) return "Just now";
    if (delta < 60 * 60 * 1000) {
        return Math.floor(delta / (60 * 1000)) + "m ago";
    }
    if (delta < 24 * 60 * 60 * 1000) {
        return Math.floor(delta / (60 * 60 * 1000)) + "h ago";
    }
    return formatSupportTicketDate(value);
}

export function getSupportTicketIdFromHash(hash) {
    const encoded = String(hash || "").replace(/^#/, "");
    if (!encoded) return "";
    try {
        return decodeURIComponent(encoded);
    } catch (error) {
        return encoded;
    }
}

export function filterSupportTickets(tickets, query, status = "all") {
    const normalizedQuery = String(query || "")
        .trim()
        .replace(/^#/, "")
        .toLowerCase();

    return (Array.isArray(tickets) ? tickets : []).filter((ticket) => {
        const statusMatches = status === "all" || ticket.status === status;
        if (!statusMatches) return false;
        if (!normalizedQuery) return true;

        return [ticket.ticketNumber, ticket.subject].some((value) =>
            String(value || "").toLowerCase().includes(normalizedQuery),
        );
    });
}

const SUPPORT_TICKET_ATTACHMENT_NOTE =
    /\s*---\s*\r?\nReferenced attachments:\s*([^\r\n]+)\s*$/i;

function normalizeLegacyAttachmentNames(description) {
    const noteMatch = String(description || "").match(
        SUPPORT_TICKET_ATTACHMENT_NOTE,
    );
    return noteMatch
        ? noteMatch[1]
              .split(",")
              .map((name) => name.trim())
              .filter(Boolean)
        : [];
}

export function stripSupportTicketAttachmentNote(content) {
    const value = String(content || "");
    const noteMatch = value.match(SUPPORT_TICKET_ATTACHMENT_NOTE);
    return (noteMatch ? value.slice(0, noteMatch.index) : value).trim();
}

export function getSupportTicketAttachments(ticket) {
    const storedAttachments = Array.isArray(ticket?.attachments)
        ? ticket.attachments.filter(Boolean)
        : [];
    if (storedAttachments.length) return storedAttachments;

    return normalizeLegacyAttachmentNames(ticket?.description).map((name) => ({
        name,
    }));
}

export function getSupportTicketCardDetails(ticket) {
    const ticketDescription = String(ticket?.description || "");
    const legacyAttachmentCount = normalizeLegacyAttachmentNames(
        ticketDescription,
    ).length;
    const storedAttachments = Array.isArray(ticket?.attachments)
        ? ticket.attachments
        : [];

    return {
        description: stripSupportTicketAttachmentNote(ticketDescription),
        attachmentCount:
            storedAttachments.length || legacyAttachmentCount,
    };
}

export function buildSupportTicketTimeline(ticket) {
    if (!ticket) return [];

    const items = [];
    if (ticket.description) {
        items.push({
            type: "message",
            key: "ticket-created",
            author: "player",
            authorName: ticket.nickname || "You",
            content: stripSupportTicketAttachmentNote(ticket.description),
            attachments: getSupportTicketAttachments(ticket),
            timestamp: ticket.createdAt,
            initial: true,
            order: 0,
        });
    }

    (ticket.messages || []).forEach((message, index) => {
        items.push({
            ...message,
            type: "message",
            key: "message-" + index,
            order: index + 1,
        });
    });

    (ticket.statusHistory || []).forEach((entry, index) => {
        items.push({
            ...entry,
            type: "event",
            key: "status-" + index,
            order: 10000 + index,
        });
    });

    return items.sort((left, right) => {
        const timeDelta =
            new Date(left.timestamp).getTime() -
            new Date(right.timestamp).getTime();
        return timeDelta || left.order - right.order;
    });
}
